/**
 * MediaBatchDetailPage — Phase D1 édito (2026-09-21)
 *
 * Détail d'un lot d'importation médias :
 *   - En-tête : nom du lot, compteur, 2 CTA (Importer / Ajouter depuis la médiathèque).
 *   - Filtres : Tous | Par jour | Par lieu | Non classés.
 *   - Grille : aperçu, date EXIF, jour assigné, lieu éditorial, statut d'usage.
 *   - Actions multi-sélection : assigner à un jour, détacher du lot, remettre en non-classées.
 *
 * RÉUTILISE :
 *   - Endpoint `/api/photos/upload` existant pour l'import Cloudinary (une seule copie binaire).
 *   - Endpoint `/api/photos` pour lister la médiathèque (sélecteur multi).
 *   - Scanner Phase B pour le statut d'usage (déjà exposé par media-enriched).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  getBatchMediaEnriched,
  attachMediaToBatch,
  detachMediaFromBatch,
  reingestBatchUnclassified,
  assignDayBulk,
  setMediaLocationOverride,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  ArrowLeft, Upload, Images, Loader2, RefreshCw, MapPin, Calendar, Filter,
  Link2Off, MoveRight, Ship,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

function StatusBadge({ status }) {
  const map = {
    used: { label: "Utilisé", cls: "bg-emerald-100 text-emerald-800" },
    social_reserved: { label: "Social", cls: "bg-slate-100 text-slate-800" },
    orphan: { label: "Orphelin", cls: "bg-amber-100 text-amber-800" },
  };
  const s = map[status] || { label: status || "—", cls: "bg-muted text-muted-foreground" };
  return <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
}

function SourceBadge({ source }) {
  const map = {
    manual: { label: "manuel", cls: "bg-primary/10 text-primary" },
    exif: { label: "EXIF", cls: "bg-indigo-100 text-indigo-800" },
    exif_suggested: { label: "EXIF ?", cls: "bg-indigo-50 text-indigo-700" },
    none: { label: "non classé", cls: "bg-amber-50 text-amber-800" },
  };
  const s = map[source] || map.none;
  return <span className={`text-[10px] px-1.5 py-0.5 rounded ${s.cls}`}>{s.label}</span>;
}

export default function MediaBatchDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | by_day | by_place | unclassified
  const [selected, setSelected] = useState(() => new Set());
  const [openUpload, setOpenUpload] = useState(false);
  const [openPicker, setOpenPicker] = useState(false);
  const [libraryPhotos, setLibraryPhotos] = useState([]);
  const [pickerSelection, setPickerSelection] = useState(() => new Set());
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bulkDay, setBulkDay] = useState("");
  const [locDialog, setLocDialog] = useState(null); // {mediaId, initial}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getBatchMediaEnriched(id, { limit: 1000, skip: 0 });
      setData(r.data);
      setSelected(new Set());
    } catch (e) {
      toast.error("Impossible de charger le lot : " + (e?.response?.data?.detail || e?.message || "erreur"));
      nav("/dashboard/medias/lots");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const days = data?.days || [];
  const batch = data?.batch || {};
  const trip = data?.trip;

  const counters = data?.counters || { by_day: {}, unclassified: 0 };

  const filtered = useMemo(() => {
    if (filter === "unclassified") return items.filter((i) => !i.trip_day_id);
    return items;
  }, [items, filter]);

  const groupedByDay = useMemo(() => {
    const groups = new Map();
    for (const it of items) {
      const key = it.trip_day_id || "__unclassified__";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(it);
    }
    return groups;
  }, [items]);

  const groupedByPlace = useMemo(() => {
    const groups = new Map();
    for (const it of items) {
      const key = it.location_label || "Lieu non identifié";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(it);
    }
    return groups;
  }, [items]);

  const toggle = (mid) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(mid)) n.delete(mid); else n.add(mid);
      return n;
    });
  };
  const toggleAll = (list) => {
    setSelected((prev) => {
      const ids = list.map((i) => i.id);
      const allIn = ids.every((x) => prev.has(x));
      const n = new Set(prev);
      if (allIn) ids.forEach((x) => n.delete(x));
      else ids.forEach((x) => n.add(x));
      return n;
    });
  };

  // ---- Upload multi via /api/photos/upload (UNE SEULE COPIE BINAIRE) ----
  const doUpload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        try {
          const r = await axios.post(`${API}/photos/upload`, form);
          uploaded.push(r.data.id);
        } catch (e) {
          toast.error(`Échec ${f.name} : ${e?.response?.data?.detail || e?.message}`);
        }
      }
      if (uploaded.length > 0) {
        // Rattachement au lot + ingest incrémental automatique côté backend
        const r = await attachMediaToBatch(id, uploaded);
        const rep = r.data?.ingest || {};
        toast.success(
          `${uploaded.length} média(s) importé(s) — ventilation : ${rep.updated ?? 0} affecté(s), ${rep.unchanged ?? 0} inchangé(s).`
        );
      }
      setOpenUpload(false);
      await load();
    } finally {
      setUploading(false);
    }
  };

  // ---- Sélecteur multi médiathèque ----
  const openLibrary = async () => {
    try {
      const r = await axios.get(`${API}/photos`, { params: { limit: 200 } });
      setLibraryPhotos(r.data?.photos || r.data || []);
      setPickerSelection(new Set());
      setOpenPicker(true);
    } catch (e) {
      toast.error("Impossible de charger la médiathèque.");
    }
  };
  const confirmAttach = async () => {
    if (pickerSelection.size === 0) return;
    setBusy(true);
    try {
      const r = await attachMediaToBatch(id, Array.from(pickerSelection));
      const rep = r.data?.ingest || {};
      toast.success(
        `${pickerSelection.size} média(s) rattaché(s) — ventilation : ${rep.updated ?? 0} affecté(s), ${rep.unchanged ?? 0} inchangé(s).`
      );
      setOpenPicker(false);
      await load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e?.message || "unknown"));
    } finally {
      setBusy(false);
    }
  };

  // ---- Actions bulk ----
  const bulkAssignDay = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      await assignDayBulk(id, {
        media_ids: Array.from(selected),
        trip_day_id: bulkDay || null,
      });
      toast.success(`${selected.size} média(s) affecté(s) (override manuel).`);
      await load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e?.message || "unknown"));
    } finally {
      setBusy(false);
    }
  };
  const bulkDetach = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Détacher ${selected.size} média(s) du lot ? Les médias restent dans la médiathèque.`)) return;
    setBusy(true);
    try {
      const r = await detachMediaFromBatch(id, Array.from(selected));
      toast.success(`${r.data?.detached_media ?? 0} média(s) détaché(s).`);
      await load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e?.message || "unknown"));
    } finally {
      setBusy(false);
    }
  };
  const reingest = async () => {
    setBusy(true);
    try {
      const r = await reingestBatchUnclassified(id);
      toast.success(`Réanalyse ciblée : ${r.data?.updated ?? 0} affecté(s), ${r.data?.unchanged ?? 0} inchangé(s), ${r.data?.skipped_manual ?? 0} override manuel préservé(s).`);
      await load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e?.message || "unknown"));
    } finally {
      setBusy(false);
    }
  };

  const submitLocation = async (label) => {
    if (!locDialog) return;
    setBusy(true);
    try {
      await setMediaLocationOverride(locDialog.mediaId, {
        location_label: label || null,
        source: "manual",
      });
      toast.success("Lieu éditorial mis à jour.");
      setLocDialog(null);
      await load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e?.message || "unknown"));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="p-12 flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
      </div>
    );
  }

  return (
    <div data-testid="media-batch-detail-page" className="max-w-6xl">
      <Button variant="ghost" size="sm" onClick={() => nav("/dashboard/medias/lots")} className="mb-3">
        <ArrowLeft className="h-4 w-4 mr-1" /> Lots d'importation
      </Button>

      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl text-primary flex items-center gap-3">
            <Images className="h-7 w-7" />
            {batch.name || "Lot"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {items.length} média(s){" "}
            {trip && (
              <>
                · Voyage lié : <span className="text-primary">{trip.title}</span>
                {trip.start_date && <span> · {trip.start_date}</span>}
              </>
            )}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={reingest} disabled={busy} data-testid="reingest-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Réanalyser non classés
          </Button>
          <Button variant="outline" onClick={openLibrary} data-testid="attach-from-library-btn">
            <Images className="h-4 w-4 mr-2" />
            Ajouter depuis la médiathèque
          </Button>
          <Button onClick={() => setOpenUpload(true)} data-testid="upload-to-batch-btn">
            <Upload className="h-4 w-4 mr-2" />
            Importer de nouvelles photos
          </Button>
        </div>
      </header>

      {/* Compteurs journée */}
      {days.length > 0 && (
        <section className="mb-4 flex gap-2 flex-wrap" data-testid="day-counters">
          {days.map((d) => {
            const c = counters.by_day?.[d.id] || 0;
            return (
              <div key={d.id} className="rounded-full border border-border px-3 py-1 text-xs bg-white flex items-center gap-2">
                <Calendar className="h-3 w-3 text-primary" />
                Jour {d.day_number} — <span className="text-muted-foreground">{d.stop_name || d.location_label || d.title || "—"}</span>
                <span className="font-medium text-primary">· {c}</span>
              </div>
            );
          })}
          <div className="rounded-full border border-amber-300 px-3 py-1 text-xs bg-amber-50 text-amber-800 flex items-center gap-2">
            Non classées <span className="font-medium">· {counters.unclassified || 0}</span>
          </div>
        </section>
      )}

      {/* Filtres */}
      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList data-testid="batch-filter-tabs">
          <TabsTrigger value="all"><Filter className="h-4 w-4 mr-1" /> Tous ({items.length})</TabsTrigger>
          <TabsTrigger value="by_day"><Calendar className="h-4 w-4 mr-1" /> Par jour</TabsTrigger>
          <TabsTrigger value="by_place"><MapPin className="h-4 w-4 mr-1" /> Par lieu</TabsTrigger>
          <TabsTrigger value="unclassified">Non classées ({counters.unclassified || 0})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Barre d'actions bulk */}
      {selected.size > 0 && (
        <div className="mb-3 bg-primary/5 border border-primary/30 rounded-md p-3 flex items-center gap-3 flex-wrap" data-testid="bulk-actions-bar">
          <span className="text-sm">{selected.size} sélectionné(s)</span>
          <div className="flex items-center gap-2">
            <Select value={bulkDay} onValueChange={setBulkDay}>
              <SelectTrigger className="w-56" data-testid="bulk-day-select">
                <SelectValue placeholder="Choisir un jour…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Non classée —</SelectItem>
                {days.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    Jour {d.day_number} — {d.stop_name || d.location_label || d.title || ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={() => {
                if (bulkDay === "__none__") { setBulkDay(""); bulkAssignDay(); }
                else bulkAssignDay();
              }}
              disabled={busy}
              data-testid="bulk-assign-day-btn"
            >
              <MoveRight className="h-4 w-4 mr-1" /> Affecter (manuel)
            </Button>
          </div>
          <Button size="sm" variant="destructive" onClick={bulkDetach} disabled={busy} data-testid="bulk-detach-btn">
            <Link2Off className="h-4 w-4 mr-1" /> Détacher du lot
          </Button>
        </div>
      )}

      {/* Rendu selon filtre */}
      {items.length === 0 ? (
        <div className="p-12 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
          Ce lot ne contient aucun média pour l'instant. Utilisez « Importer » ou « Ajouter depuis la médiathèque ».
        </div>
      ) : filter === "by_day" ? (
        <div className="space-y-6">
          {[...days.map((d) => [d.id, d]), ["__unclassified__", null]].map(([key, day]) => {
            const list = groupedByDay.get(key) || [];
            if (list.length === 0) return null;
            const title = day ? `Jour ${day.day_number} — ${day.stop_name || day.title || ""}` : "Non classées";
            return <MediaGroup key={key} title={title} list={list} selected={selected} toggle={toggle} toggleAll={toggleAll} onLoc={(m) => setLocDialog({ mediaId: m.id, initial: m.location_label || "" })} />;
          })}
        </div>
      ) : filter === "by_place" ? (
        <div className="space-y-6">
          {[...groupedByPlace.entries()].map(([label, list]) => (
            <MediaGroup key={label} title={label} list={list} selected={selected} toggle={toggle} toggleAll={toggleAll} onLoc={(m) => setLocDialog({ mediaId: m.id, initial: m.location_label || "" })} />
          ))}
        </div>
      ) : (
        <MediaGroup title="" list={filtered} selected={selected} toggle={toggle} toggleAll={toggleAll} onLoc={(m) => setLocDialog({ mediaId: m.id, initial: m.location_label || "" })} />
      )}

      {/* Upload dialog */}
      <Dialog open={openUpload} onOpenChange={setOpenUpload}>
        <DialogContent data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Importer des photos dans le lot</DialogTitle>
            <DialogDescription>
              Les fichiers sont uploadés une seule fois via le pipeline existant, puis
              automatiquement rattachés à ce lot. Métadonnées EXIF/GPS analysées à la volée
              (ventilation par jour selon la date de prise de vue).
            </DialogDescription>
          </DialogHeader>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => doUpload(Array.from(e.target.files || []))}
            disabled={uploading}
            data-testid="upload-input"
          />
          {uploading && (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Upload en cours…
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenUpload(false)} disabled={uploading}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media picker dialog */}
      <Dialog open={openPicker} onOpenChange={setOpenPicker}>
        <DialogContent className="max-w-3xl" data-testid="library-picker-dialog">
          <DialogHeader>
            <DialogTitle>Ajouter des médias existants à ce lot</DialogTitle>
            <DialogDescription>
              Les médias sélectionnés seront rattachés au lot (aucune duplication). La ventilation
              par jour est calculée automatiquement pour chaque média rattaché.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 md:grid-cols-4 gap-2 max-h-[440px] overflow-auto p-1">
            {libraryPhotos.map((p) => {
              const url = p.cloudinary_url || p.public_cloudinary_url;
              const sel = pickerSelection.has(p.id);
              return (
                <div key={p.id} className={`relative rounded-lg overflow-hidden border-2 ${sel ? "border-emerald-500" : "border-transparent"}`}>
                  {url && <img src={url} alt="" className="w-full h-24 object-cover" loading="lazy" />}
                  <button
                    onClick={() => {
                      setPickerSelection((prev) => {
                        const n = new Set(prev);
                        if (n.has(p.id)) n.delete(p.id); else n.add(p.id);
                        return n;
                      });
                    }}
                    className="absolute inset-x-0 bottom-0 bg-black/55 text-white text-[10px] py-1"
                    data-testid={`picker-toggle-${p.id}`}
                  >
                    {sel ? "✓ sélectionné" : "+ ajouter"}
                  </button>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenPicker(false)} disabled={busy}>Annuler</Button>
            <Button onClick={confirmAttach} disabled={busy || pickerSelection.size === 0} data-testid="picker-confirm-btn">
              Rattacher {pickerSelection.size} média(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Location override dialog */}
      <Dialog open={!!locDialog} onOpenChange={(v) => !v && setLocDialog(null)}>
        <DialogContent data-testid="location-dialog">
          <DialogHeader>
            <DialogTitle>Lieu éditorial</DialogTitle>
            <DialogDescription>
              Corrigez le lieu affiché pour ce média. Ce libellé humain n'expose aucune
              coordonnée GPS et est traité comme un override manuel prioritaire.
            </DialogDescription>
          </DialogHeader>
          <LocForm initial={locDialog?.initial || ""} onCancel={() => setLocDialog(null)} onSubmit={submitLocation} busy={busy} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LocForm({ initial, onCancel, onSubmit, busy }) {
  const [value, setValue] = useState(initial || "");
  return (
    <>
      <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Ex. Bergen, Flåm…" data-testid="loc-input" />
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={busy}>Annuler</Button>
        <Button onClick={() => onSubmit(value.trim())} disabled={busy} data-testid="loc-submit">Enregistrer</Button>
      </DialogFooter>
    </>
  );
}

function MediaGroup({ title, list, selected, toggle, toggleAll, onLoc }) {
  if (!list || list.length === 0) return null;
  return (
    <section className="bg-white border border-border rounded-lg overflow-hidden">
      {title && (
        <header className="p-3 flex items-center justify-between border-b border-border">
          <h3 className="font-heading text-sm text-primary">{title}</h3>
          <Button variant="ghost" size="sm" onClick={() => toggleAll(list)} data-testid={`group-toggle-${title || "all"}`}>
            {list.every((i) => selected.has(i.id)) ? "Tout désélectionner" : "Tout sélectionner"}
          </Button>
        </header>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 p-2">
        {list.map((m) => (
          <MediaCard key={m.id} media={m} selected={selected.has(m.id)} onToggle={() => toggle(m.id)} onLoc={() => onLoc(m)} />
        ))}
      </div>
    </section>
  );
}

function MediaCard({ media, selected, onToggle, onLoc }) {
  return (
    <div className={`relative rounded-md overflow-hidden border-2 ${selected ? "border-emerald-500" : "border-transparent"} bg-secondary/30`} data-testid={`media-card-${media.id}`}>
      {media.url ? (
        <img src={media.url} alt="" className="w-full h-28 object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-28 bg-muted flex items-center justify-center text-xs text-muted-foreground">
          <Ship className="h-4 w-4" />
        </div>
      )}
      <div className="p-1.5 text-[10px] leading-tight space-y-0.5">
        <div className="text-muted-foreground">{fmtDate(media.date_taken)}</div>
        <div className="flex items-center justify-between gap-1">
          <div className="truncate">
            {media.day_number ? `J${media.day_number}` : "—"}
            {media.day_label ? ` · ${media.day_label}` : ""}
          </div>
          <SourceBadge source={media.day_assignment_source} />
        </div>
        <button className="w-full text-left truncate hover:underline" onClick={onLoc} data-testid={`loc-edit-${media.id}`}>
          <MapPin className="h-3 w-3 inline mr-0.5" /> {media.location_label || "Lieu non identifié"}
        </button>
        <div className="flex items-center justify-between">
          <StatusBadge status={media.usage_status} />
          <button
            onClick={onToggle}
            className="text-primary text-[10px] hover:underline"
            data-testid={`toggle-${media.id}`}
          >
            {selected ? "✓" : "+"}
          </button>
        </div>
      </div>
    </div>
  );
}
